// ============================================================
// App.js — die absolute Schaltzentrale der ganzen React-App
// Hier wird alles zusammengesteckt: wer eingeloggt ist, welches
// Theme gerade läuft, welche Seite angezeigt wird — basically
// das Fundament auf dem der Rest steht. Ohne das hier läuft nix.
// ============================================================

import React, { createContext, useContext, useState, useEffect, useCallback, Suspense, lazy } from 'react';
import "@/App.css";
// BrowserRouter = der Typ der dafür sorgt dass die URL sich ändert ohne Seiten-Reload
// Routes/Route = wie eine Weiche im Zug — welche URL zeigt welche Seite
// Navigate = redirect, schickt den User irgendwohin
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
// axios = der giga HTTP-Client — macht API-Calls zum Backend viel komfortabler als fetch
import axios from "axios";
import MailerLiteTasks from "@/components/MailerLiteTasks";
import { Toaster } from "@/components/ui/sonner";
// toast = die kleinen Benachrichtigungs-Popups oben rechts — "Gespeichert!", "Fehler!" etc.
import { toast } from "sonner";

// Seiten-Komponenten — jede ist eine eigene "Seite" die angezeigt wird
// LoginPage bleibt eager (wird immer sofort gebraucht), die restlichen Seiten
// werden per React.lazy code-gesplittet — so lädt der Browser beim ersten
// Aufruf nur das JS für /login statt das komplette App-Bundle inkl. Dashboard,
// Kontakte, Settings und Pressespiegel auf einmal.
import LoginPage from "@/pages/LoginPage";
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
// Kontakte ist die Hauptseite. Ihr Chunk wird deshalb schon geladen, BEVOR React ihn braucht:
//  - direkt beim Start, wenn man auf /contacts landet: er lädt parallel zur Token-Prüfung (/auth/me)
//    statt erst danach (sonst Wasserfall: Bundle -> /auth/me -> Chunk -> Kontakte laden);
//  - sonst (Login, Dashboard …) im Leerlauf, damit der Wechsel dorthin sofort geht.
// Und ist der Chunk beim Rendern schon da, wird die Seite DIREKT gerendert statt über React.lazy:
// lazy wirft beim ersten Rendern immer, auch wenn der Import längst fertig ist, dabei erscheint der
// Lade-Spinner, und React hält den fertigen Inhalt danach künstlich bis zu 300ms zurück (interne
// Drosselung, damit Spinner nicht flackern). Gemessen war das der größte Einzelposten im Ladewasserfall.
let contactsPageModule = null;
const loadContactsPage = () => import("@/pages/ContactsPage").then((module) => {
  contactsPageModule = module;
  return module;
});
const LazyContactsPage = lazy(loadContactsPage);
const ContactsPage = (props) => {
  const Page = contactsPageModule ? contactsPageModule.default : LazyContactsPage;
  return <Page {...props} />;
};
if (typeof window !== "undefined") {
  try {
    if (window.location.pathname.startsWith("/contacts") && localStorage.getItem("token")) {
      loadContactsPage();
    } else if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => { loadContactsPage(); }, { timeout: 4000 });
    }
  } catch (error) {
    // Preload ist nur eine Optimierung — nie ein Grund, die App nicht zu starten
  }
}
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const PressespiegelPage = lazy(() => import("@/pages/PressespiegelPage"));
const IssuesPage = lazy(() => import("@/pages/IssuesPage"));
// Layout = der äußere Rahmen mit Navigation — bleibt immer gleich, nur der Inhalt wechselt
import Layout from "@/components/Layout";

// Backend-URL kommt aus der .env-Datei — in dev http://localhost:8000, live die echte Domain
// So müssen wir die URL nie hardcoden und können einfach zwischen Umgebungen wechseln
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// /api ist das Prefix für alle Backend-Endpunkte — wird überall in der App genutzt
export const API = `${BACKEND_URL}/api`;

// ============================================================
// AUTH CONTEXT — der globale Einlog-Zustand der ganzen App
// Statt den User-Status durch 10 Komponenten als Props zu reichen
// (= "Prop Drilling", mega nervig), packen wir's in einen Context.
// Jede Komponente kann dann einfach useAuth() aufrufen — ez.
// ============================================================
const AuthContext = createContext(null);

// useAuth = der Hook den jede Komponente nutzt um an den Auth-State zu kommen
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  // user = das eingeloggte User-Objekt (null wenn nicht eingeloggt)
  const [user, setUser] = useState(null);
  // Token aus localStorage laden — überlebt Seiten-Reload, wird bei jedem API-Call mitgeschickt
  const [token, setToken] = useState(localStorage.getItem('token'));
  // loading = true während wir prüfen ob der gespeicherte Token noch gültig ist
  const [loading, setLoading] = useState(true);

  // login() — schickt E-Mail + Passwort ans Backend, speichert Token bei Erfolg
  // gibt true zurück wenn's geklappt hat, false wenn nicht (damit LoginPage weiß was zu tun ist)
  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      // Token in localStorage = bleibt nach Seiten-Reload erhalten, kein Login-Screen nach F5
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      toast.success(`Willkommen, ${userData.name}!`);
      return true;
    } catch (error) {
      // Fehlermeldung vom Backend nehmen oder Fallback — immer auf Deutsch zeigen
      toast.error(error.response?.data?.detail || 'Login fehlgeschlagen');
      return false;
    }
  }, []);

  // register() — nur beim allerersten Start möglich (danach macht Admin neue User)
  const register = useCallback(async (email, password, name) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, password, name });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      toast.success('Registrierung erfolgreich!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
      return false;
    }
  }, []);

  // logout() — Token löschen, User auf null setzen, fertig
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    toast.info('Abgemeldet');
  }, []);

  // completeLogin() — wird nach 2FA-Verifikation aufgerufen (2-Faktor-Login)
  // normaler Login schickt erst temp_token, nach 2FA-Code kommt dann der echte Token
  const completeLogin = useCallback((accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
  }, []);

  // updateUser() — User-Objekt lokal updaten ohne API-Call (z.B. nach Einstellungsänderung)
  const updateUser = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  }, []);

  // Beim Start der App: prüfen ob der gespeicherte Token noch gültig ist
  // Wenn ja → User laden und direkt einloggen (kein Login-Screen nach Neustart)
  // Wenn nein → Token löschen, zur Login-Seite
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          // /auth/me gibt das User-Objekt zurück wenn der Token gültig ist
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch {
          // Token abgelaufen oder ungültig — raus damit
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  // axios-Interceptor — hängt den Auth-Token an JEDEN Request automatisch dran
  // So muss keine Komponente selbst daran denken, das "Authorization: Bearer ..."-Header zu setzen
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    // cleanup: Interceptor wieder entfernen wenn Token sich ändert (z.B. nach logout)
    return () => axios.interceptors.request.eject(interceptor);
  }, [token]);

  return (
    // Alle Kinder-Komponenten bekommen Zugriff auf user, token, login, logout etc.
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, completeLogin, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================
// THEME CONTEXT — Dark Mode / Light Mode für die ganze App
// Tailwind steuert das über eine CSS-Klasse auf dem <html>-Tag
// ============================================================
const ThemeContext = createContext(null);

export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Zuerst schauen ob User schon eine Präferenz gespeichert hat
    const saved = localStorage.getItem('theme');
    // Wenn nix gespeichert: System-Einstellung checken (Dark Mode im OS respektieren — nice touch)
    return saved || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  });

  useEffect(() => {
    // Theme in localStorage speichern damit's nach Neustart erhalten bleibt
    localStorage.setItem('theme', theme);
    // Tailwind schaltet dark mode über die 'dark'-Klasse auf dem html-Element — so einfach ist das
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // toggleTheme() — einfach zwischen dark und light hin- und herwechseln
  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// ============================================================
// PROTECTED ROUTE — Türsteher für alle Seiten hinter dem Login
// Wer nicht eingeloggt ist, kommt hier nicht rein — wird zur
// Login-Seite redirectet. Ez. Kein Umgehen möglich.
// ============================================================
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  // location speichern damit nach Login direkt zur richtigen Seite weitergeleitet wird
  // (User wollte z.B. /contacts, muss aber erst einloggen → nach Login direkt zu /contacts)
  const location = useLocation();

  if (loading) {
    // Spinner anzeigen während Token gegen API geprüft wird — kein kurzes "Login-Flash"
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // 'from' mitgeben damit nach Login direkt zur ursprünglichen Seite weitergeleitet wird
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Alles gut — Seite anzeigen
  return children;
};

// Zusätzlicher Türsteher für Seiten, die nur Admin/Editor/Developer sehen dürfen (z.B. den
// internen Issue-Tracker) — schickt alle anderen Rollen zurück zu /contacts statt 404/leer.
const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  const isAllowed = user?.role === 'admin' || user?.role === 'editor' || user?.role === 'developer';
  if (!isAllowed) return <Navigate to="/contacts" replace />;
  return children;
};

// ============================================================
// TOUR CONTEXT — Zustand für die interaktive Hilfe-Tour (Klick aufs
// Fragezeichen-Symbol neben dem Dark-Mode-Toggle, siehe Layout.js).
// Muss INNERHALB von <BrowserRouter> sitzen (anders als Theme/Auth),
// weil "andere Seite erklären" per useNavigate() dorthin wechselt und
// die Tour dort automatisch startet, sobald ihr erstes Zielelement im
// DOM ist (pendingTourId). Die eigentliche Overlay-Darstellung
// (Spotlight + Tooltip-Karte) übernimmt TourOverlay.js, gerendert in
// Layout.js — der Context hier ist bewusst reine Zustandsverwaltung.
// ============================================================
const TourContext = createContext(null);

export const useTour = () => useContext(TourContext);

const TourProvider = ({ children }) => {
  const [activeTourId, setActiveTourId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  // pendingTourId: Tour, die auf der NÄCHSTEN Seite starten soll, sobald sie gemountet ist —
  // gesetzt beim Wechsel von "andere Seite erklären" im Hilfe-Menü, siehe goToTour().
  const [pendingTourId, setPendingTourId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const startTour = useCallback((id) => {
    setPendingTourId(null);
    setActiveTourId(id);
    setStepIndex(0);
  }, []);

  // Tour auf einer anderen Route starten: erst hinnavigieren, dann (in TourOverlay) automatisch
  // loslegen. Ist die Zielroute schon die aktuelle, sofort starten — kein unnötiger Reload.
  const goToTour = useCallback((id, route) => {
    if (route && route !== location.pathname) {
      setPendingTourId(id);
      navigate(route);
    } else {
      startTour(id);
    }
  }, [location.pathname, navigate, startTour]);

  const next = useCallback(() => setStepIndex((index) => index + 1), []);
  const back = useCallback(() => setStepIndex((index) => Math.max(0, index - 1)), []);
  const stop = useCallback(() => { setActiveTourId(null); setStepIndex(0); setPendingTourId(null); }, []);
  const clearPending = useCallback(() => setPendingTourId(null), []);

  return (
    <TourContext.Provider value={{
      activeTourId, stepIndex, pendingTourId, currentRoute: location.pathname,
      startTour, goToTour, next, back, stop, clearPending,
    }}>
      {children}
    </TourContext.Provider>
  );
};

// Fallback während eine per React.lazy code-gesplittete Seite nachgeladen wird
// (nur beim allerersten Aufruf dieser Route spürbar, danach ist der Chunk gecacht)
const PageLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// ============================================================
// APP — der Haupt-Component der alles zusammensetzt
// ThemeProvider außen, AuthProvider innen, dann der Router
// Reihenfolge ist wichtig: Theme > Auth > Router > Routes
// ============================================================
function App() {
  return (
    // ThemeProvider ganz außen — alles kriegt das Theme
    <ThemeProvider>
      {/* AuthProvider gibt überall Zugriff auf Login-Status */}
      <AuthProvider>
        <BrowserRouter>
          <TourProvider>
          <Routes>
            {/* Login-Seite ist öffentlich — kein ProtectedRoute nötig */}
            <Route path="/login" element={<LoginPage />} />

            {/* Alle anderen Seiten sind geschützt — ProtectedRoute kickt nicht-eingeloggte raus */}
            <Route path="/" element={
              <ProtectedRoute>
                {/* Layout = Navigation + Sidebar, bleibt auf allen Unterseiten gleich */}
                <Layout />
              </ProtectedRoute>
            }>
              {/* Root-URL "/" redirectet direkt zu /contacts — das ist die Hauptseite */}
              <Route index element={<Navigate to="/contacts" replace />} />
              <Route path="dashboard" element={
                <Suspense fallback={<PageLoadingFallback />}><DashboardPage /></Suspense>
              } />
              <Route path="contacts" element={
                <Suspense fallback={<PageLoadingFallback />}><ContactsPage /></Suspense>
              } />
              <Route path="pressespiegel" element={
                <Suspense fallback={<PageLoadingFallback />}><PressespiegelPage /></Suspense>
              } />
              <Route path="issues" element={
                <AdminRoute>
                  <Suspense fallback={<PageLoadingFallback />}><IssuesPage /></Suspense>
                </AdminRoute>
              } />
              <Route path="settings" element={
                <Suspense fallback={<PageLoadingFallback />}><SettingsPage /></Suspense>
              } />
            </Route>
          </Routes>
          </TourProvider>
        </BrowserRouter>
        {/* Toaster = globale Toast-Benachrichtigungen, oben rechts, mit Farben für Erfolg/Fehler/Info */}
        <MailerLiteTasks />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
